import type { BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { createApi, retry } from '@reduxjs/toolkit/query/react'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { customAxios } from '@/utils/axiosHelper'
import config from '@/config'
import { shouldBailout } from '../helpers'
import type {
  TrunkThread,
  TrunkThreadWithMessages,
  TrunkListThreadsResponse,
  TrunkCreateThreadRequest,
  TrunkCreateThreadResponse,
  TrunkMessage,
  TrunkCreateMessageRequest,
  TrunkCreateMessageResponse,
  TrunkUpdateThreadRequest,
  TrunkDeleteResponse,
  TrunkListMessagesResponse,
  TrunkPresignRequest,
  TrunkPresignResponse,
  TrunkConfirmAssetResponse,
  TrunkListAssetsResponse,
  TrunkUpdateAssetRequest,
  TrunkAsset,
  TrunkVoiceSession,
  TrunkVoiceTokenRequest,
  TrunkVoiceTokenResponse,
  TrunkVoiceSpeaker,
  TrunkVoiceInfo,
  WorkspaceListResponse,
  WorkspaceFileContentResponse,
  WorkspaceWriteRequest,
  WorkspaceWriteResponse,
  WorkspaceDeleteResponse,
  WorkspaceMkdirRequest,
  WorkspaceMkdirResponse,
  WorkspacePublishRequest,
  WorkspacePublishResponse,
  TrunkJob,
  TrunkScheduledJob,
  HubspotDealContext,
  TrunkCreateScheduledJobRequest,
  SupportIssuesResponse,
  SupportCaseDetailResponse,
  SupportQueueResponse,
  SupportStaffResponse,
  SupportSavedViewsResponse,
  SupportSavedView,
  SupportTagsResponse,
  SupportTag,
  SupportSearchResponse,
  ReportingSummaryRequest,
  ReportingSummaryResponse,
  ReviewsReportRequest,
  ReviewsReportResponse,
  SupportCaseContextPanel,
  SupportContextVersion,
  SupportAgentsResponse,
  OnboardingThreadsResponse,
  CreateOnboardingThreadRequest,
  CreateOnboardingThreadResponse,
  OnboardingThreadWithMessages,
  GeneralCrmThreadsResponse,
  CreateGeneralCrmThreadRequest,
  CreateGeneralCrmThreadResponse,
  GeneralCrmThreadWithMessages,
  ListProposalsResponse,
  ProposalMutationResponse,
  AssetViewUrlResponse,
  KbVideoListQuery,
  KbVideoListResponse,
  KbVideoResponse,
  KbVideoFacetsResponse,
  KbVideoSearchResponse,
  KbVideoTranscriptResponse,
  KbVideoTagsResponse,
  KbVideoIngestRequest,
  KbVideoIngestResponse,
  KbVideoInput,
  KbVideoUpsertResponse,
  KbVideoDeleteResponse,
  KbVideoRelatedResponse,
  KbWatchEventsRequest,
  KbWatchEventsResponse,
  KbVideoStatsResponse,
  KbVideoWatchersResponse,
  KbProductionScript,
  KbProductionSpecInput,
  KbVideoProduction,
  KbVideoProductionCreateResponse,
  KbVideoProductionListResponse,
  KbVideoProductionResponse,
  KbVideoVoicesResponse,
  KbVideoCatalogueResponse,
  KbProductionRemakeInput,
  KbVideoTheme,
  OrderingReportQuery,
  OrderingReportResponse,
} from './trunk.types'

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
    headers?: AxiosRequestConfig['headers']
    /**
     * Passed through for binary endpoints. Without it axios decodes a PDF as
     * UTF-8 text and the bytes are quietly corrupted: the request succeeds,
     * the file saves, and it will not open.
     */
    responseType?: AxiosRequestConfig['responseType']
  }> =>
  async ({ url, method, data, params, headers, responseType }) => {
    try {
      let axiosUrl = baseUrl + url
      if (url.startsWith('https://')) {
        axiosUrl = url
      }

      const result = await customAxios({
        url: axiosUrl,
        method,
        data,
        params,
        headers,
        ...(responseType ? { responseType } : {}),
      })

      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError

      if (shouldBailout(err)) {
        retry.fail(err)
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

// ── Scheduled tasks (enquiries workspace; trunk owns the record) ─────────────

export type ScheduledDeliverTo = {
  channel: 'task' | 'notification' | 'email'
  to?: string
}

export interface ScheduledTaskLastRun {
  at: string
  outcome: 'succeeded' | 'failed' | 'skipped'
  summary: string | null
  error: string | null
  delivered_to: string[]
}

export interface ScheduledTask {
  id: string
  org_id: string
  serial: string | null
  owner: { oauth_id: string; name: string | null; email: string | null }
  kind: string
  kind_label: string
  name: string
  instructions: string | null
  cron: string
  timezone: string
  spoken: string
  status: 'active' | 'paused'
  next_fire_at: string | null
  last_fire_at: string | null
  deliver_to: ScheduledDeliverTo[]
  may_act: boolean
  last_run: ScheduledTaskLastRun | null
  consecutive_failures: number
  created_by: { oauth_id: string; name: string | null }
  created_at: string
  updated_at: string
  /** a single run at run_at rather than a schedule; paused once it has fired */
  one_off: boolean
  run_at: string | null
  finished_at: string | null
}

export interface ScheduledTaskSetup {
  name: string
  kind: string
  kind_label: string
  preset_summary: string | null
  cron: string
  timezone: string
  spoken: string
  next_runs: string[]
  instructions: string | null
  what_it_will_do: string
  deliver_to: ScheduledDeliverTo[]
  may_act: boolean
  serial: string | null
  one_off: boolean
  run_at: string | null
  /** set when the time was relative ("in 5 minutes"): the clock starts at confirm */
  run_in_seconds: number | null
}

export interface ScheduledTaskPreset {
  kind: string
  label: string
  summary: string
  default_cron: string
  spoken: string
}

export interface ScheduledTaskRun {
  id: string
  fired_at: string
  outcome: string
  run_id: string | null
  summary: string | null
  error: string | null
  delivered_to: string[]
}

export interface ProposeScheduledTaskBody {
  text: string
  name?: string | null
  kind?: string | null
  cron?: string | null
  timezone?: string | null
  serial?: string | null
  deliver_to?: ScheduledDeliverTo[] | null
  may_act?: boolean | null
}

// ── Workflows (enquiries workspace; graphs live in trunk's Mastra store) ─────

export type WorkflowTrigger = {
  event: string
  where?: { party_size_gte?: number; serial?: string; type?: string }
} | null

export interface WorkflowRef {
  id: string
  org_id: string
  serial: string | null
  name: string
  description: string | null
  definition_id: string
  version: number
  status: 'draft' | 'active' | 'archived'
  trigger: WorkflowTrigger
  may_act: boolean
  /** the SOP as the venue wrote it: the words the current version was compiled from */
  source_text: string | null
  /** the rule set every run reads: reply_within_hours, chase_after_days, deposit_percent, owner_policy and the venue's own */
  rules: WorkflowRules | null
  author_oauth_id: string | null
  created_at: string
  updated_at: string
}

export type WorkflowRules = Record<string, number | string | boolean>

export interface WorkflowDefinition {
  id: string
  description: string | null
  graph: unknown[]
  input_schema: unknown
  status: string
  metadata:
    | (Record<string, unknown> & {
        name?: string
        issue_list?: Array<{ code?: string; message: string; path?: string }>
        compile_error?: string
      })
    | null
  /** failed: the model could not write it; the version is spent, compile again makes the next */
  state: 'ready' | 'failed'
  error: string | null
  created_at: string
  updated_at: string
}

/** A file on an enquiry (W1): the index row; the bytes are in the workspace bucket. */
export interface EnquiryFileRow {
  id: string
  enquiry_id: string
  key: string
  name: string
  mime: string
  size: number
  kind:
    | 'pdf'
    | 'document'
    | 'spreadsheet'
    | 'image'
    | 'text'
    | 'audio'
    | 'other'
  uploaded_by: string | null
  uploaded_by_name: string | null
  created_at: string
}

/** POST /workflows/compile answers with where the compile will land; poll the definition. */
export interface CompileJob {
  lineage_id: string
  version: number
  definition_id: string
  state: 'compiling'
}

export interface CompiledWorkflow {
  definition_id: string
  lineage_id: string
  version: number
  name: string
  description: string | null
  graph: unknown[]
  issues: Array<{ code?: string; message: string; path?: string }>
}

export interface WorkflowRunRef {
  id: string
  workflow_id: string
  definition_id: string
  org_id: string
  enquiry_id: string
  mastra_run_id: string
  status: 'running' | 'waiting' | 'success' | 'failed' | 'stopped'
  current_step: string | null
  waiting_for: { step: string; events: string[]; until: string | null } | null
  started_by: string | null
  started_at: string
  finished_at: string | null
  error: string | null
}

export type SuggestedTask = {
  title: string
  notes: string
  due_at: string
  why: string
}
export type EnquirySuggestion =
  | { kind: 'summary'; summary: string }
  | {
      kind: 'reply'
      subject: string | null
      body_text: string
      body_html: string
      rationale: string
    }
  | { kind: 'tasks'; tasks: SuggestedTask[] }

const trunkApi = createApi({
  reducerPath: 'trunk',
  baseQuery: retry(axiosBaseQuery({ baseUrl: config.url.trunk }), {
    maxRetries: 2,
  }),
  tagTypes: [
    'Thread',
    'Message',
    'Asset',
    'WorkspaceFile',
    'Job',
    'Graph',
    'SupportCase',
    'SupportTag',
    'SupportView',
    'SupportContext',
    'Proposal',
    'OnboardingThread',
    'GeneralCrmThread',
    'KbVideo',
    'ScheduledTask',
    'Workflow',
    'EnquiryFile',
  ],
  endpoints: (build) => ({
    /**
     * A HubSpot deal read as onboarding context.
     *
     * Lives in trunk because trunk holds the HubSpot token; morpheus has
     * none. Read-only, and @stampede.ai-gated upstream, because a deal
     * carries commercial terms and the customer's private notes.
     *
     * `deal` is whatever was pasted: a record URL or a bare id. The server
     * parses it, so a wrong paste comes back as a 400 rather than a lookup
     * of the wrong object.
     */
    getOnboardingHubspotDeal: build.query<
      { data: HubspotDealContext },
      { deal: string }
    >({
      query: ({ deal }) => ({
        method: 'GET',
        url: `/internal/onboarding/hubspot-deal/${encodeURIComponent(deal)}`,
      }),
    }),
    /**
     * A menu's trade, from the ClickHouse warehouse rather than Mouse.
     *
     * WHY NOT `mouseApi.getMenuReport`. That one fans out to 28 queries
     * against Mouse's connection pool of ten, which is the pool guests pay
     * through, and it exhausted it on 3 October. It also only answers for a
     * single day. This is eleven keyed reads against a store nobody transacts
     * against, and it takes a RANGE.
     *
     * FIGURES ONLY. No prose, no titles, no tense: those stay with the
     * narrator so the same range always produces the same words.
     *
     * NEVER RETRIED beyond the slice default, and deliberately not widened:
     * the whole point is to stop a reporting read being able to hurt anything.
     */
    getOrderingReport: build.query<OrderingReportResponse, OrderingReportQuery>(
      {
        query: ({ orgId, menuId, from, to, bucket = 15, sections }) => ({
          method: 'GET',
          url: '/ordering-report',
          params: {
            org_id: orgId,
            menu_id: menuId,
            from,
            to,
            bucket,
            ...(sections?.length ? { sections: sections.join(',') } : {}),
          },
        }),
      }
    ),
    listThreads: build.query<
      TrunkListThreadsResponse,
      { orgId: string; limit?: number; offset?: number }
    >({
      query: ({ orgId, limit = 50, offset = 0 }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/threads`,
        params: { limit, offset },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.threads.map(({ id }) => ({
                type: 'Thread' as const,
                id,
              })),
              { type: 'Thread', id: 'LIST' },
            ]
          : [{ type: 'Thread', id: 'LIST' }],
    }),

    getThread: build.query<
      TrunkThreadWithMessages,
      { orgId: string; threadId: string }
    >({
      query: ({ orgId, threadId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/threads/${threadId}`,
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'Thread', id: result.id },
              // Backend may return the thread without a `messages`
              // array on this endpoint — guard so `providesTags`
              // doesn't crash the RTK Query cache write.
              ...(result.messages ?? []).map(({ id }) => ({
                type: 'Message' as const,
                id,
              })),
            ]
          : [],
    }),

    createThread: build.mutation<
      TrunkCreateThreadResponse,
      { orgId: string; data?: TrunkCreateThreadRequest }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/threads`,
        data: data ?? {},
      }),
      invalidatesTags: [{ type: 'Thread', id: 'LIST' }],
    }),

    updateThread: build.mutation<
      TrunkThread,
      { orgId: string; threadId: string; data: TrunkUpdateThreadRequest }
    >({
      query: ({ orgId, threadId, data }) => ({
        method: 'PUT',
        url: `/organisations/${orgId}/threads/${threadId}`,
        data,
      }),
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: 'Thread', id: threadId },
        { type: 'Thread', id: 'LIST' },
      ],
    }),

    deleteThread: build.mutation<
      TrunkDeleteResponse,
      { orgId: string; threadId: string }
    >({
      query: ({ orgId, threadId }) => ({
        method: 'DELETE',
        url: `/organisations/${orgId}/threads/${threadId}`,
      }),
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: 'Thread', id: threadId },
        { type: 'Thread', id: 'LIST' },
      ],
    }),

    listMessages: build.query<
      TrunkListMessagesResponse,
      { orgId: string; threadId: string; limit?: number; cursor?: string }
    >({
      query: ({ orgId, threadId, limit = 1000, cursor }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/threads/${threadId}/messages`,
        params: { limit, ...(cursor ? { cursor } : {}) },
      }),
      providesTags: (result, _error, { threadId }) =>
        result
          ? [
              ...result.messages.map(({ id }) => ({
                type: 'Message' as const,
                id,
              })),
              { type: 'Message', id: `LIST-${threadId}` },
            ]
          : [{ type: 'Message', id: `LIST-${threadId}` }],
    }),

    createMessage: build.mutation<
      TrunkCreateMessageResponse,
      { orgId: string; threadId: string; data: TrunkCreateMessageRequest }
    >({
      query: ({ orgId, threadId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/threads/${threadId}/messages`,
        data,
      }),
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: 'Message', id: `LIST-${threadId}` },
        { type: 'Thread', id: threadId },
      ],
    }),

    deleteMessage: build.mutation<
      TrunkDeleteResponse,
      { orgId: string; threadId: string; messageId: string }
    >({
      query: ({ orgId, threadId, messageId }) => ({
        method: 'DELETE',
        url: `/organisations/${orgId}/threads/${threadId}/messages/${messageId}`,
      }),
      invalidatesTags: (_result, _error, { threadId, messageId }) => [
        { type: 'Message', id: messageId },
        { type: 'Message', id: `LIST-${threadId}` },
        { type: 'Thread', id: threadId },
      ],
    }),

    presignAsset: build.mutation<
      TrunkPresignResponse,
      { orgId: string; data: TrunkPresignRequest }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/assets/presign`,
        data,
      }),
    }),

    confirmAsset: build.mutation<
      TrunkConfirmAssetResponse,
      { orgId: string; assetId: string }
    >({
      query: ({ orgId, assetId }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/assets/${assetId}/confirm`,
      }),
      invalidatesTags: (_result, _error, { assetId }) => [
        { type: 'Asset', id: assetId },
      ],
    }),

    /**
     * Server-side background removal. Trunk fetches the source
     * URL itself (no browser CORS), runs the image through the
     * `@imgly/background-removal-node` model, and returns the
     * cutout as base64. The caller is responsible for pushing
     * the bytes into the gallery (`uploadImage` helper) — that
     * way the cutout lives on `files.stampede.ai` alongside
     * every other gallery image. See `useRemoveImageBackground`.
     */
    removeAssetBackground: build.mutation<
      {
        filename: string
        mimeType: string
        size: number
        dataBase64: string
      },
      {
        orgId: string
        url: string
        /**
         * Optional vertical band (top/bottom 0–1 fractions of
         * image height) inside which bg-removal applies. Outside
         * the band the original pixels are kept. Omitted = full-
         * image bg removal (legacy default).
         */
        band?: { top: number; bottom: number }
      }
    >({
      query: ({ orgId, url, band }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/assets/remove-background`,
        data: { url, ...(band ? { band } : {}) },
      }),
    }),

    /**
     * Server-side crop. Coords are normalised (0–1 fractions of
     * source dimensions) so the FE doesn't need to know the
     * original pixel size. Trunk reads metadata, projects the
     * rect onto real pixels, runs `sharp.extract`, returns the
     * cropped bytes as base64. Same response shape as
     * `removeAssetBackground` so callers can share the upload-
     * to-gallery path.
     */
    cropAsset: build.mutation<
      {
        filename: string
        mimeType: string
        size: number
        dataBase64: string
      },
      {
        orgId: string
        url: string
        x: number
        y: number
        width: number
        height: number
      }
    >({
      query: ({ orgId, url, x, y, width, height }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/assets/crop`,
        data: { url, x, y, width, height },
      }),
    }),

    /**
     * Generic image transform endpoint. `operation` selects the
     * sharp pipeline (monochrome, invert, brand-tint,
     * recolor-logo, resize, smart-resize, badge, text-overlay).
     * `params` is operation-specific — see trunk's
     * `routes/assets.ts /assets/transform` for the schema. Same
     * response shape as `cropAsset` / `removeAssetBackground`
     * so the FE can reuse the upload-to-gallery flow.
     */
    transformAsset: build.mutation<
      {
        filename: string
        mimeType: string
        size: number
        width?: number
        height?: number
        dataBase64: string
      },
      {
        orgId: string
        operation:
          | 'monochrome'
          | 'invert'
          | 'brand-tint'
          | 'recolor-logo'
          | 'resize'
          | 'smart-resize'
          | 'badge'
          | 'text-overlay'
          | 'mask-with-shape'
          | 'fade'
        url: string
        params?: Record<string, unknown>
      }
    >({
      query: ({ orgId, operation, url, params }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/assets/transform`,
        data: { operation, url, params },
      }),
    }),

    listThreadAssets: build.query<
      TrunkListAssetsResponse,
      { orgId: string; threadId: string }
    >({
      query: ({ orgId, threadId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/threads/${threadId}/assets`,
      }),
      providesTags: (result, _error, { threadId }) =>
        result
          ? [
              ...result.assets.map(({ id }) => ({
                type: 'Asset' as const,
                id,
              })),
              { type: 'Asset', id: `LIST-${threadId}` },
            ]
          : [{ type: 'Asset', id: `LIST-${threadId}` }],
    }),

    updateAsset: build.mutation<
      TrunkAsset,
      { orgId: string; assetId: string; data: TrunkUpdateAssetRequest }
    >({
      query: ({ orgId, assetId, data }) => ({
        method: 'PATCH',
        url: `/organisations/${orgId}/assets/${assetId}`,
        data,
      }),
      invalidatesTags: (_result, _error, { assetId }) => [
        { type: 'Asset', id: assetId },
      ],
    }),

    deleteAsset: build.mutation<
      { ok: boolean },
      { orgId: string; assetId: string }
    >({
      query: ({ orgId, assetId }) => ({
        method: 'DELETE',
        url: `/organisations/${orgId}/assets/${assetId}`,
      }),
      invalidatesTags: (_result, _error, { assetId }) => [
        { type: 'Asset', id: assetId },
      ],
    }),

    // Voice Chat Endpoints
    createVoiceSession: build.mutation<
      TrunkVoiceSession,
      { threadId?: string; resourceId?: string }
    >({
      query: (data) => ({
        method: 'POST',
        url: `/voice/session`,
        data,
      }),
    }),

    getVoiceToken: build.mutation<
      TrunkVoiceTokenResponse,
      TrunkVoiceTokenRequest
    >({
      query: (data) => ({
        method: 'POST',
        url: `/voice/token`,
        data,
      }),
    }),

    getVoiceSpeakers: build.query<{ speakers: TrunkVoiceSpeaker[] }, void>({
      query: () => ({
        method: 'GET',
        url: `/voice/speakers`,
      }),
    }),

    getVoiceInfo: build.query<TrunkVoiceInfo, void>({
      query: () => ({
        method: 'GET',
        url: `/voice/info`,
      }),
    }),

    // Workspace file management
    listWorkspaceFiles: build.query<
      WorkspaceListResponse,
      { orgId: string; path?: string }
    >({
      query: ({ orgId, path = '/' }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/workspace/files`,
        params: { path },
      }),
      providesTags: (_result, _error, { orgId, path }) => [
        { type: 'WorkspaceFile', id: `LIST-${orgId}-${path || '/'}` },
      ],
    }),

    readWorkspaceFile: build.query<
      WorkspaceFileContentResponse,
      { orgId: string; path: string }
    >({
      query: ({ orgId, path }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/workspace/file`,
        params: { path },
      }),
      providesTags: (_result, _error, { orgId, path }) => [
        { type: 'WorkspaceFile', id: `FILE-${orgId}-${path}` },
      ],
    }),

    writeWorkspaceFile: build.mutation<
      WorkspaceWriteResponse,
      { orgId: string; data: WorkspaceWriteRequest }
    >({
      query: ({ orgId, data }) => ({
        method: 'PUT',
        url: `/organisations/${orgId}/workspace/file`,
        data,
      }),
      invalidatesTags: (_result, _error, { orgId, data }) => {
        const dir = data.path.substring(0, data.path.lastIndexOf('/')) || '/'
        return [
          { type: 'WorkspaceFile', id: `LIST-${orgId}-${dir}` },
          { type: 'WorkspaceFile', id: `FILE-${orgId}-${data.path}` },
        ]
      },
    }),

    deleteWorkspaceFile: build.mutation<
      WorkspaceDeleteResponse,
      { orgId: string; path: string }
    >({
      query: ({ orgId, path }) => ({
        method: 'DELETE',
        url: `/organisations/${orgId}/workspace/file`,
        params: { path },
      }),
      invalidatesTags: (_result, _error, { orgId, path }) => {
        const dir = path.substring(0, path.lastIndexOf('/')) || '/'
        return [{ type: 'WorkspaceFile', id: `LIST-${orgId}-${dir}` }]
      },
    }),

    // ── Files on an enquiry (W1) ──────────────────────────────────────────────
    listEnquiryFileFolders: build.query<
      {
        folders: Array<{
          enquiry_id: string
          files: number
          last_at: string | null
        }>
      },
      { orgId: string; serial?: string | null }
    >({
      query: ({ orgId, serial }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/enquiries/files/folders`,
        params: serial ? { serial } : {},
      }),
      providesTags: [{ type: 'EnquiryFile', id: 'FOLDERS' }],
    }),
    listEnquiryFiles: build.query<
      { files: EnquiryFileRow[] },
      { orgId: string; enquiryId: string }
    >({
      query: ({ orgId, enquiryId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/enquiries/${encodeURIComponent(enquiryId)}/files`,
      }),
      providesTags: (_r, _e, { enquiryId }) => [
        { type: 'EnquiryFile', id: enquiryId },
      ],
    }),
    presignEnquiryFile: build.mutation<
      { uploadUrl: string; key: string; name: string },
      {
        orgId: string
        enquiryId: string
        name: string
        mime: string
        size: number
      }
    >({
      query: ({ orgId, enquiryId, ...body }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/enquiries/${encodeURIComponent(enquiryId)}/files/presign`,
        data: body,
      }),
    }),
    confirmEnquiryFile: build.mutation<
      { file: EnquiryFileRow },
      {
        orgId: string
        enquiryId: string
        key: string
        name: string
        mime: string
        size: number
        serial?: string | null
      }
    >({
      query: ({ orgId, enquiryId, ...body }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/enquiries/${encodeURIComponent(enquiryId)}/files/confirm`,
        data: body,
      }),
      invalidatesTags: (_r, _e, { enquiryId }) => [
        { type: 'EnquiryFile', id: enquiryId },
        { type: 'EnquiryFile', id: 'FOLDERS' },
      ],
    }),
    getEnquiryFileUrl: build.query<
      { url: string; name: string; mime: string },
      { orgId: string; enquiryId: string; fileId: string; download?: boolean }
    >({
      query: ({ orgId, enquiryId, fileId, download }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/enquiries/${encodeURIComponent(enquiryId)}/files/${encodeURIComponent(fileId)}/url`,
        params: download ? { download: '1' } : {},
      }),
    }),
    deleteEnquiryFile: build.mutation<
      { ok: boolean },
      { orgId: string; enquiryId: string; fileId: string }
    >({
      query: ({ orgId, enquiryId, fileId }) => ({
        method: 'DELETE',
        url: `/organisations/${orgId}/enquiries/${encodeURIComponent(enquiryId)}/files/${encodeURIComponent(fileId)}`,
      }),
      invalidatesTags: (_r, _e, { enquiryId }) => [
        { type: 'EnquiryFile', id: enquiryId },
      ],
    }),
    presignWorkspaceDownload: build.query<
      { downloadUrl: string },
      { orgId: string; path: string }
    >({
      query: ({ orgId, path }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/workspace/presign-download`,
        params: { path },
      }),
    }),
    /** The Coordinator proposes on an enquiry (zion A3): a summary, the next reply, or the tasks that are missing. */
    suggestForEnquiry: build.mutation<
      EnquirySuggestion,
      { orgId: string; enquiryId: string; kind: 'summary' | 'reply' | 'tasks' }
    >({
      query: ({ orgId, enquiryId, kind }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/enquiries/${encodeURIComponent(enquiryId)}/suggest`,
        data: { kind },
      }),
    }),
    transcribeRecording: build.mutation<
      { queued: boolean; job_id: string | null },
      {
        orgId: string
        enquiryId: string
        noteId: string
        key: string
        mime: string
      }
    >({
      query: ({ orgId, enquiryId, noteId, key, mime }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/enquiries/${encodeURIComponent(enquiryId)}/notes/${encodeURIComponent(noteId)}/transcribe`,
        data: { key, mime },
      }),
    }),
    presignWorkspaceUpload: build.mutation<
      { uploadUrl: string; path: string; s3Key: string },
      { orgId: string; data: { path: string; mimeType: string; size: number } }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workspace/presign`,
        data,
      }),
    }),

    renameWorkspaceFile: build.mutation<
      { success: boolean; from: string; to: string },
      { orgId: string; data: { from: string; to: string } }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workspace/rename`,
        data,
      }),
      invalidatesTags: (_result, _error, { orgId, data }) => {
        const fromDir =
          data.from.substring(0, data.from.lastIndexOf('/')) || '/'
        const toDir = data.to.substring(0, data.to.lastIndexOf('/')) || '/'
        const tags = [
          { type: 'WorkspaceFile' as const, id: `LIST-${orgId}-${fromDir}` },
          { type: 'WorkspaceFile' as const, id: `FILE-${orgId}-${data.from}` },
        ]
        if (toDir !== fromDir) {
          tags.push({
            type: 'WorkspaceFile' as const,
            id: `LIST-${orgId}-${toDir}`,
          })
        }
        return tags
      },
    }),

    createWorkspaceDir: build.mutation<
      WorkspaceMkdirResponse,
      { orgId: string; data: WorkspaceMkdirRequest }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workspace/mkdir`,
        data,
      }),
      invalidatesTags: (_result, _error, { orgId, data }) => {
        const dir = data.path.substring(0, data.path.lastIndexOf('/')) || '/'
        return [{ type: 'WorkspaceFile', id: `LIST-${orgId}-${dir}` }]
      },
    }),

    publishWorkspaceApp: build.mutation<
      WorkspacePublishResponse,
      { orgId: string; data: WorkspacePublishRequest }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workspace/publish`,
        data,
      }),
    }),

    // Job endpoints
    listJobs: build.query<
      {
        jobs: TrunkJob[]
        pagination: {
          total: number
          page: number
          perPage: number
          hasMore: boolean
        }
      },
      {
        orgId: string
        status?: string
        type?: string
        limit?: number
        page?: number
      }
    >({
      query: ({ orgId, limit, page, ...rest }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/jobs`,
        params: { ...rest, perPage: limit || 20, page: page || 0 },
      }),
      providesTags: [{ type: 'Job', id: 'LIST' }],
    }),

    getJob: build.query<{ job: TrunkJob }, { orgId: string; jobId: string }>({
      query: ({ orgId, jobId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/jobs/${jobId}`,
      }),
      providesTags: (_result, _error, { jobId }) => [
        { type: 'Job', id: jobId },
      ],
    }),

    cancelJob: build.mutation<
      { success: boolean; message: string },
      { orgId: string; jobId: string }
    >({
      query: ({ orgId, jobId }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/jobs/${jobId}/cancel`,
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: 'Job', id: jobId },
        { type: 'Job', id: 'LIST' },
      ],
    }),

    // Graph social profile
    checkGraphOrg: build.query<
      { exists: boolean; org?: { id: string; name: string } },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/graph/org/${orgId}/check`,
      }),
    }),

    getGraphSocialProfile: build.query<
      {
        found: boolean
        customer?: Record<string, unknown>
        stats?: {
          total_bookings: number
          completed_bookings: number
          cancelled_bookings: number
          no_show_bookings: number
          total_visits: number
          total_spend_pence: number
          connection_count: number
          review_count: number
          avg_rating: number | null
          wifi_sessions: number
          loyalty_points: number
        }
        connections?: {
          dining_companions: {
            email: string
            first_name: string
            last_name: string
            dined_count: number
          }[]
          referred: { email: string; first_name: string; last_name: string }[]
          referred_by: {
            email: string
            first_name: string
            last_name: string
          }[]
        }
        venues?: {
          serial: string
          name: string
          visit_count: number
          total_spend: number
          first_visit: string
          last_visit: string
        }[]
        bookings?: Record<string, unknown>[]
        reviews?: Record<string, unknown>[]
        top_items?: { name: string; count: number; spend: number }[]
        loyalty?: Record<string, unknown>[]
        tags?: string[]
      },
      { orgId: string; email: string }
    >({
      query: ({ orgId, email }) => ({
        method: 'GET',
        url: `/graph/social/${orgId}`,
        params: { email },
      }),
    }),

    // Migration pre-flight
    inspectMigrationCsv: build.mutation<
      {
        success: boolean
        total_rows: number
        headers: string[]
        sample_rows: Record<string, string>[]
        suggested_mapping: Record<string, string>
        unmapped_headers: string[]
        error?: string
      },
      { orgId: string; data: { workspace_path: string } }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/jobs/migration/inspect`,
        data,
      }),
    }),

    validateMigration: build.mutation<
      {
        success: boolean
        total_csv_rows: number
        valid_bookings: number
        error_count: number
        warning_count: number
        date_range: { earliest: string; latest: string } | null
        table_report: {
          venue_tables: number
          rows_with_tables: number
          rows_without_tables: number
          tables_matched: number
          tables_unmatched: number
          auto_assign_needed: number
        }
        venue_areas: { id: string; name: string; table_count: number }[]
        issues: {
          row: number
          field: string
          severity: string
          message: string
        }[]
        sample_bookings: unknown[]
        normalized_bookings: unknown[]
        error?: string
      },
      {
        orgId: string
        data: {
          serial: string
          workspace_path: string
          column_mapping: Record<string, string>
          default_duration?: number
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/jobs/migration/validate`,
        data,
      }),
    }),

    // Migration job creation (with pre-validated bookings)
    createMigrationJob: build.mutation<
      {
        success: boolean
        job: {
          id: string
          type: string
          status: string
          totalBookings: number
          createdAt: string
        }
      },
      {
        orgId: string
        data: {
          serial: string
          venue_name?: string
          workspace_path: string
          thread_id?: string
          normalized_bookings: unknown[]
          options?: {
            skip_duplicates?: boolean
            auto_assign_tables?: boolean
            area_ids?: string[]
          }
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/jobs/migration`,
        data,
      }),
      invalidatesTags: [{ type: 'Job', id: 'LIST' }],
    }),

    // Scheduled job endpoints
    listScheduledJobs: build.query<
      { jobs: TrunkScheduledJob[] },
      { orgId: string; status?: string; action?: string }
    >({
      query: ({ orgId, ...params }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/scheduled-jobs`,
        params,
      }),
      providesTags: [{ type: 'Job', id: 'SCHEDULED_LIST' }],
    }),

    createScheduledJob: build.mutation<
      { success: boolean; job: TrunkScheduledJob },
      { orgId: string; data: TrunkCreateScheduledJobRequest }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/scheduled-jobs`,
        data,
      }),
      invalidatesTags: [{ type: 'Job', id: 'SCHEDULED_LIST' }],
    }),

    cancelScheduledJob: build.mutation<
      { success: boolean },
      { orgId: string; jobId: string }
    >({
      query: ({ orgId, jobId }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/scheduled-jobs/${jobId}/cancel`,
      }),
      invalidatesTags: [{ type: 'Job', id: 'SCHEDULED_LIST' }],
    }),

    // ── Graph Backfill ─────────────────────────────────────────
    getGraphBackfillStatus: build.query<
      {
        org_id: string
        backfilled: boolean
        syncing?: boolean
        started_at?: string
        first_backfill_at?: string
        last_updated_at?: string
        type?: string
      },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/falkordb/backfill-status?org_id=${orgId}`,
      }),
      providesTags: ['Graph'],
    }),

    triggerGraphBackfill: build.mutation<
      { message: string; org_id: string; job_id: string; stream: string },
      {
        orgId: string
        env: string
        label?: string
        force_full?: boolean
        notify_email?: string
        notify_name?: string
        chat_url?: string
      }
    >({
      query: ({
        orgId,
        env,
        label,
        force_full,
        notify_email,
        notify_name,
        chat_url,
      }) => ({
        method: 'POST',
        url: `/graph/backfill`,
        data: {
          org_id: orgId,
          env,
          label,
          force_full,
          notify_email,
          notify_name,
          chat_url,
        },
      }),
      invalidatesTags: ['Graph'],
    }),

    triggerGraphUpdate: build.mutation<
      {
        message: string
        org_id: string
        job_id: string
        since: string
        until: string | null
        stream: string
      },
      {
        orgId: string
        since?: string
        until?: string
        notify_email?: string
        notify_name?: string
        chat_url?: string
      }
    >({
      query: ({
        orgId,
        since,
        until,
        notify_email,
        notify_name,
        chat_url,
      }) => ({
        method: 'POST',
        url: `/falkordb/trigger-update`,
        data: {
          org_id: orgId,
          since,
          until,
          notify_email,
          notify_name,
          chat_url,
        },
      }),
      invalidatesTags: ['Graph'],
    }),

    getCostSummary: build.query<
      {
        total_cost_usd: number
        total_messages: number
        total_tokens: number
        daily_usage: {
          tokens_used: number
          token_limit: number
          messages_used: number
          message_limit: number
        } | null
      },
      { orgId: string; resourceId?: string }
    >({
      query: ({ orgId, resourceId }) => ({
        method: 'GET',
        url: `/costs/summary?org_id=${orgId}${resourceId ? `&resource_id=${resourceId}` : ''}`,
      }),
    }),

    getThreadCost: build.query<
      {
        thread_id: string
        total_cost_usd: number
        total_tokens: number
        message_count: number
      },
      { threadId: string }
    >({
      query: ({ threadId }) => ({
        method: 'GET',
        url: `/costs/thread/${threadId}`,
      }),
    }),

    getMessageCosts: build.query<
      {
        costs: {
          messageId: string
          modelId: string
          totalTokens: number
          totalCost: number
        }[]
      },
      { threadId: string }
    >({
      query: ({ threadId }) => ({
        method: 'GET',
        url: `/costs/messages/${threadId}`,
      }),
    }),

    createDeepWorkJob: build.mutation<
      {
        success: boolean
        job: { id: string; type: string; status: string; createdAt: string }
      },
      {
        orgId: string
        task: string
        context?: string
        threadId?: string
        maxIterations?: number
      }
    >({
      query: ({ orgId, task, context, threadId, maxIterations }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/jobs/deep-work`,
        data: {
          task,
          context,
          thread_id: threadId,
          max_iterations: maxIterations,
        },
      }),
      invalidatesTags: ['Job'],
    }),

    // ── Customer Support Agent ─────────────────────────────────────
    // Scoped server-side by the bearer token (the axios interceptor
    // attaches it) — no orgId in the path.
    getSupportIssues: build.query<SupportIssuesResponse, void>({
      query: () => ({ method: 'GET', url: `/support/issues` }),
      providesTags: (result) =>
        result
          ? [
              ...result.issues.map(({ caseId }) => ({
                type: 'SupportCase' as const,
                id: caseId,
              })),
              { type: 'SupportCase', id: 'LIST' },
            ]
          : [{ type: 'SupportCase', id: 'LIST' }],
    }),

    getSupportCase: build.query<SupportCaseDetailResponse, { caseId: string }>({
      query: ({ caseId }) => ({
        method: 'GET',
        url: `/support/cases/${caseId}`,
      }),
      providesTags: (_result, _error, { caseId }) => [
        { type: 'SupportCase', id: caseId },
      ],
    }),

    resolveSupportCase: build.mutation<
      { ok: boolean; caseId: string; status: string },
      { caseId: string }
    >({
      query: ({ caseId }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/resolve`,
      }),
      invalidatesTags: (_result, _error, { caseId }) => [
        { type: 'SupportCase', id: caseId },
        { type: 'SupportCase', id: 'LIST' },
        { type: 'SupportCase', id: 'QUEUE' },
      ],
    }),

    // Staff console (admin) — the cross-customer support queue + filters.
    getSupportQueue: build.query<
      SupportQueueResponse,
      {
        status?: string
        owner?: string
        orgId?: string
        needsHuman?: boolean
        channel?: string
        tag?: string
        q?: string
      } | void
    >({
      query: (args) => ({
        method: 'GET',
        url: `/support/queue`,
        params: {
          ...(args && args.status ? { status: args.status } : {}),
          ...(args && args.owner ? { owner: args.owner } : {}),
          ...(args && args.orgId ? { orgId: args.orgId } : {}),
          ...(args && args.needsHuman ? { needsHuman: true } : {}),
          ...(args && args.channel ? { channel: args.channel } : {}),
          ...(args && args.tag ? { tag: args.tag } : {}),
          ...(args && args.q ? { q: args.q } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.queue.map(({ caseId }) => ({
                type: 'SupportCase' as const,
                id: caseId,
              })),
              { type: 'SupportCase', id: 'QUEUE' },
            ]
          : [{ type: 'SupportCase', id: 'QUEUE' }],
    }),

    claimSupportCase: build.mutation<
      { ok: boolean; caseId: string; owner: string },
      { caseId: string }
    >({
      query: ({ caseId }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/claim`,
      }),
      invalidatesTags: (_result, _error, { caseId }) => [
        { type: 'SupportCase', id: caseId },
        { type: 'SupportCase', id: 'QUEUE' },
      ],
    }),

    reopenSupportCase: build.mutation<
      { ok: boolean; caseId: string; status: string },
      { caseId: string }
    >({
      query: ({ caseId }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/reopen`,
      }),
      invalidatesTags: (_result, _error, { caseId }) => [
        { type: 'SupportCase', id: caseId },
        { type: 'SupportCase', id: 'QUEUE' },
      ],
    }),

    // ── Triage v2 — picker, assign, subscribers, bulk, saved views ──
    getSupportStaff: build.query<SupportStaffResponse, void>({
      query: () => ({ method: 'GET', url: `/support/staff` }),
    }),

    assignSupportCase: build.mutation<
      { ok: boolean; caseId: string; owner: string | null },
      {
        caseId: string
        userId: string | null
        email?: string
        firstName?: string
        lastName?: string
      }
    >({
      query: ({ caseId, ...body }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/assign`,
        data: body,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: 'SupportCase', id: caseId },
        { type: 'SupportCase', id: 'QUEUE' },
      ],
    }),

    addSupportSubscriber: build.mutation<
      { ok: boolean },
      {
        caseId: string
        userId: string
        email?: string
        firstName?: string
        lastName?: string
      }
    >({
      query: ({ caseId, ...body }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/subscribers`,
        data: body,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: 'SupportCase', id: caseId },
      ],
    }),

    removeSupportSubscriber: build.mutation<
      { ok: boolean },
      { caseId: string; userId: string }
    >({
      query: ({ caseId, userId }) => ({
        method: 'DELETE',
        url: `/support/cases/${caseId}/subscribers/${userId}`,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: 'SupportCase', id: caseId },
      ],
    }),

    batchSupportCases: build.mutation<
      { results: { caseId: string; ok: boolean; error?: string }[] },
      {
        caseIds: string[]
        action: 'assign' | 'resolve' | 'reopen' | 'tag'
        payload?: Record<string, unknown>
      }
    >({
      query: (body) => ({
        method: 'POST',
        url: `/support/cases/batch`,
        data: body,
      }),
      invalidatesTags: [{ type: 'SupportCase', id: 'QUEUE' }],
    }),

    getSupportViews: build.query<SupportSavedViewsResponse, void>({
      query: () => ({ method: 'GET', url: `/support/views` }),
      providesTags: [{ type: 'SupportView', id: 'LIST' }],
    }),
    createSupportView: build.mutation<
      { view: SupportSavedView },
      {
        name: string
        scope: 'personal' | 'team'
        filterJson: Record<string, unknown>
      }
    >({
      query: (body) => ({ method: 'POST', url: `/support/views`, data: body }),
      invalidatesTags: [{ type: 'SupportView', id: 'LIST' }],
    }),
    updateSupportView: build.mutation<
      { view: SupportSavedView },
      {
        id: string
        name?: string
        scope?: 'personal' | 'team'
        filterJson?: Record<string, unknown>
      }
    >({
      query: ({ id, ...body }) => ({
        method: 'PATCH',
        url: `/support/views/${id}`,
        data: body,
      }),
      invalidatesTags: [{ type: 'SupportView', id: 'LIST' }],
    }),
    deleteSupportView: build.mutation<{ ok: boolean }, { id: string }>({
      query: ({ id }) => ({ method: 'DELETE', url: `/support/views/${id}` }),
      invalidatesTags: [{ type: 'SupportView', id: 'LIST' }],
    }),

    // ── Tags (Epic 2) ───────────────────────────────────────────────
    getSupportTags: build.query<SupportTagsResponse, { q?: string } | void>({
      query: (args) => ({
        method: 'GET',
        url: `/support/tags`,
        params: args && args.q ? { q: args.q } : {},
      }),
      providesTags: [{ type: 'SupportTag', id: 'LIST' }],
    }),
    createSupportTag: build.mutation<
      { tag: SupportTag },
      { name: string; color?: string | null }
    >({
      query: (body) => ({ method: 'POST', url: `/support/tags`, data: body }),
      invalidatesTags: [{ type: 'SupportTag', id: 'LIST' }],
    }),
    updateSupportTag: build.mutation<
      { tag: SupportTag },
      { id: string; name?: string; color?: string | null }
    >({
      query: ({ id, ...body }) => ({
        method: 'PATCH',
        url: `/support/tags/${id}`,
        data: body,
      }),
      invalidatesTags: [
        { type: 'SupportTag', id: 'LIST' },
        { type: 'SupportCase', id: 'QUEUE' },
      ],
    }),
    deleteSupportTag: build.mutation<{ ok: boolean }, { id: string }>({
      query: ({ id }) => ({ method: 'DELETE', url: `/support/tags/${id}` }),
      invalidatesTags: [
        { type: 'SupportTag', id: 'LIST' },
        { type: 'SupportCase', id: 'QUEUE' },
      ],
    }),
    attachCaseTag: build.mutation<
      { tag: unknown },
      { caseId: string; tagId?: string; name?: string; color?: string | null }
    >({
      query: ({ caseId, ...body }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/tags`,
        data: body,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: 'SupportCase', id: caseId },
        { type: 'SupportTag', id: 'LIST' },
      ],
    }),
    detachCaseTag: build.mutation<
      { ok: boolean },
      { caseId: string; tagId: string }
    >({
      query: ({ caseId, tagId }) => ({
        method: 'DELETE',
        url: `/support/cases/${caseId}/tags/${tagId}`,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: 'SupportCase', id: caseId },
      ],
    }),

    // ── Search (Epic 3) ─────────────────────────────────────────────
    searchSupport: build.query<
      SupportSearchResponse,
      {
        q: string
        everything?: boolean
        status?: string
        owner?: string
        orgId?: string
        channel?: string
        tag?: string
      }
    >({
      query: ({ q, everything, ...rest }) => ({
        method: 'GET',
        url: `/support/search`,
        params: { q, ...(everything ? { everything: '1' } : {}), ...rest },
      }),
    }),

    // ── Context & Intelligence (Epic 4) ─────────────────────────────
    getCaseContext: build.query<SupportCaseContextPanel, { caseId: string }>({
      query: ({ caseId }) => ({
        method: 'GET',
        url: `/support/cases/${caseId}/context`,
      }),
      providesTags: (_r, _e, { caseId }) => [
        { type: 'SupportContext', id: caseId },
      ],
    }),
    getContextVersions: build.query<
      { versions: SupportContextVersion[] },
      { caseId: string }
    >({
      query: ({ caseId }) => ({
        method: 'GET',
        url: `/support/cases/${caseId}/context/versions`,
      }),
      providesTags: (_r, _e, { caseId }) => [
        { type: 'SupportContext', id: caseId },
      ],
    }),
    regenerateContext: build.mutation<
      { ok: boolean; latestContext: unknown },
      { caseId: string }
    >({
      query: ({ caseId }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/context/regenerate`,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: 'SupportContext', id: caseId },
      ],
    }),
    caseRundown: build.mutation<{ text: string }, { caseId: string }>({
      query: ({ caseId }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/rundown`,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: 'SupportContext', id: caseId },
      ],
    }),
    helperChat: build.mutation<
      { text: string },
      { caseId: string; message: string }
    >({
      query: ({ caseId, message }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/helper/chat`,
        data: { message },
      }),
    }),
    opsChat: build.mutation<
      { text: string; stats?: Record<string, number> },
      { message: string }
    >({
      query: (body) => ({
        method: 'POST',
        url: `/support/helper/chat`,
        data: body,
      }),
    }),
    getSupportAgents: build.query<SupportAgentsResponse, void>({
      query: () => ({ method: 'GET', url: `/support/agents` }),
    }),
    specialistChat: build.mutation<
      { text: string },
      { caseId: string; key: string; message: string }
    >({
      query: ({ caseId, key, message }) => ({
        method: 'POST',
        url: `/support/cases/${caseId}/agents/${key}/chat`,
        data: { message },
      }),
    }),
    // ── Onboarding Copilot ────────────────────────────────────────────────

    listOnboardingThreads: build.query<OnboardingThreadsResponse, void>({
      query: () => ({ method: 'GET', url: '/agents/onboarding/threads' }),
      providesTags: (result) =>
        result
          ? [
              ...result.threads.map(({ id }) => ({
                type: 'OnboardingThread' as const,
                id,
              })),
              { type: 'OnboardingThread' as const, id: 'LIST' },
            ]
          : [{ type: 'OnboardingThread' as const, id: 'LIST' }],
    }),

    createOnboardingThread: build.mutation<
      CreateOnboardingThreadResponse,
      CreateOnboardingThreadRequest
    >({
      query: (data) => ({
        method: 'POST',
        url: '/agents/onboarding/threads',
        data,
      }),
      invalidatesTags: [{ type: 'OnboardingThread', id: 'LIST' }],
    }),

    getOnboardingThread: build.query<
      OnboardingThreadWithMessages,
      { id: string }
    >({
      query: ({ id }) => ({
        method: 'GET',
        url: `/agents/onboarding/threads/${id}`,
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'OnboardingThread', id }],
    }),

    renameOnboardingThread: build.mutation<
      CreateOnboardingThreadResponse,
      { id: string; title: string }
    >({
      query: ({ id, title }) => ({
        method: 'PATCH',
        url: `/agents/onboarding/threads/${id}`,
        data: { title },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'OnboardingThread', id },
        { type: 'OnboardingThread', id: 'LIST' },
      ],
    }),

    deleteOnboardingThread: build.mutation<{ ok: boolean }, { id: string }>({
      query: ({ id }) => ({
        method: 'DELETE',
        url: `/agents/onboarding/threads/${id}`,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'OnboardingThread', id },
        { type: 'OnboardingThread', id: 'LIST' },
      ],
    }),

    // ── Durable General CRM (Alba) threads ────────────────────────────────
    // Agent-scoped thread pool (agentId='general-crm-agent') for the durable
    // chat surface, distinct from the org-generic /organisations/:orgId/threads
    // used by the legacy /chat page.
    listGeneralCrmThreads: build.query<GeneralCrmThreadsResponse, void>({
      query: () => ({ method: 'GET', url: '/agents/general-crm/threads' }),
      providesTags: (result) =>
        result
          ? [
              ...result.threads.map(({ id }) => ({
                type: 'GeneralCrmThread' as const,
                id,
              })),
              { type: 'GeneralCrmThread' as const, id: 'LIST' },
            ]
          : [{ type: 'GeneralCrmThread' as const, id: 'LIST' }],
    }),
    createGeneralCrmThread: build.mutation<
      CreateGeneralCrmThreadResponse,
      CreateGeneralCrmThreadRequest
    >({
      query: (data) => ({
        method: 'POST',
        url: '/agents/general-crm/threads',
        data,
      }),
      invalidatesTags: [{ type: 'GeneralCrmThread', id: 'LIST' }],
    }),
    getGeneralCrmThread: build.query<
      GeneralCrmThreadWithMessages,
      { id: string }
    >({
      query: ({ id }) => ({
        method: 'GET',
        url: `/agents/general-crm/threads/${id}`,
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'GeneralCrmThread', id }],
    }),
    renameGeneralCrmThread: build.mutation<
      CreateGeneralCrmThreadResponse,
      { id: string; title: string }
    >({
      query: ({ id, title }) => ({
        method: 'PATCH',
        url: `/agents/general-crm/threads/${id}`,
        data: { title },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'GeneralCrmThread', id },
        { type: 'GeneralCrmThread', id: 'LIST' },
      ],
    }),
    deleteGeneralCrmThread: build.mutation<{ ok: boolean }, { id: string }>({
      query: ({ id }) => ({
        method: 'DELETE',
        url: `/agents/general-crm/threads/${id}`,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'GeneralCrmThread', id },
        { type: 'GeneralCrmThread', id: 'LIST' },
      ],
    }),

    // Proposals (sign-off queue). `params` mirrors the /internal/proposals filters:
    // agentId, ownerUserId, accountRef, status (csv), actionableBy, due/created windows.
    listProposals: build.query<
      ListProposalsResponse,
      {
        agentId?: string
        accountRef?: string
        status?: string
        actionableBy?: string
        dueBefore?: string
        dueAfter?: string
        limit?: number
      } | void
    >({
      query: (params) => ({
        method: 'GET',
        url: '/internal/proposals',
        params: params ?? {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.proposals.map(({ id }) => ({
                type: 'Proposal' as const,
                id,
              })),
              { type: 'Proposal' as const, id: 'LIST' },
            ]
          : [{ type: 'Proposal' as const, id: 'LIST' }],
    }),

    approveProposal: build.mutation<ProposalMutationResponse, { id: string }>({
      query: ({ id }) => ({
        method: 'POST',
        url: `/internal/proposals/${id}/approve`,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Proposal', id },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    rejectProposal: build.mutation<ProposalMutationResponse, { id: string }>({
      query: ({ id }) => ({
        method: 'POST',
        url: `/internal/proposals/${id}/reject`,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Proposal', id },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    snoozeProposal: build.mutation<
      ProposalMutationResponse,
      { id: string; until: string }
    >({
      query: ({ id, until }) => ({
        method: 'POST',
        url: `/internal/proposals/${id}/snooze`,
        data: { until },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Proposal', id },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    dismissProposal: build.mutation<ProposalMutationResponse, { id: string }>({
      query: ({ id }) => ({
        method: 'POST',
        url: `/internal/proposals/${id}/dismiss`,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Proposal', id },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    /**
     * Full Bhuna reporting. One call returns every domain for an org over an
     * arbitrary date range and venue set.
     *
     * `grain` is a GROUP BY server-side, not a different table, so any two
     * dates work -- including "last 37 days" or "this quarter vs last".
     */
    getReportingSummary: build.query<
      ReportingSummaryResponse,
      ReportingSummaryRequest
    >({
      query: ({ orgId, from, to, serials, grain, domains }) => ({
        method: 'GET',
        url: '/reporting/summary',
        params: {
          org_id: orgId,
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(serials?.length ? { serials: serials.join(',') } : {}),
          ...(grain ? { grain } : {}),
          ...(domains?.length ? { domains: domains.join(',') } : {}),
        },
      }),
    }),

    /**
     * Reviews reporting. The group scorecard, per-venue aspect scores, named
     * staff, reply performance on matured cohorts only, and the coverage every
     * section of the page states.
     *
     * Ratings come back PER PLATFORM and are never merged: gating makes Google
     * and internal different populations. Quotes come back as review ids with
     * the text hydrated on the way out, because the warehouse holds no review
     * text at all.
     */
    getReviewsReport: build.query<ReviewsReportResponse, ReviewsReportRequest>({
      query: ({ orgId, from, to, serials, platforms, slaDays }) => ({
        method: 'GET',
        url: '/reporting/reviews/summary',
        params: {
          org_id: orgId,
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(serials?.length ? { serials: serials.join(',') } : {}),
          ...(platforms?.length ? { platforms: platforms.join(',') } : {}),
          ...(slaDays ? { sla_days: slaDays } : {}),
        },
      }),
    }),

    /**
     * The report as a PDF, rendered by trunk through a headless Chromium.
     *
     * A blob rather than a link, because the endpoint needs the bearer token
     * and an anchor href cannot carry one. The download is triggered in
     * transformResponse so a caller only has to fire the query.
     *
     * NOT cached: `keepUnusedDataFor: 0`. Holding a 160KB binary in the store
     * for every range a user browses is a slow memory leak, and the second
     * click should re-render against whatever the warehouse holds now.
     */
    getReviewsReportPdf: build.query<
      { bytes: number },
      ReviewsReportRequest & { filename?: string }
    >({
      query: ({ orgId, from, to, serials, platforms, slaDays }) => ({
        method: 'GET',
        url: '/reporting/reviews/pdf',
        responseType: 'blob',
        params: {
          org_id: orgId,
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(serials?.length ? { serials: serials.join(',') } : {}),
          ...(platforms?.length ? { platforms: platforms.join(',') } : {}),
          ...(slaDays ? { sla_days: slaDays } : {}),
        },
      }),
      keepUnusedDataFor: 0,
      transformResponse(data: Blob, _meta, arg) {
        const url = window.URL.createObjectURL(
          data instanceof Blob
            ? data
            : new Blob([data], { type: 'application/pdf' })
        )
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', arg.filename ?? 'reviews-report.pdf')
        document.body.appendChild(link)
        link.click()
        link.remove()
        // Revoked on the next tick: Safari needs the object URL to still exist
        // when the click is handled, and leaving it alive leaks the blob.
        setTimeout(() => window.URL.revokeObjectURL(url), 1000)
        return { bytes: data.size ?? 0 }
      },
    }),

    // ── Knowledge base videos ──────────────────────────────────────
    // Trunk owns the manifest, the filters and the search scoring, so the
    // browser sends a query and renders the answer. Scoped server-side by the
    // bearer token (the axios interceptor attaches it) — no orgId in the path,
    // because the film library is global rather than per tenant.
    //
    // Debounce `q` at the call site: every keystroke is a request now, where
    // the old knowledge base page re-scored a local copy of the catalogue.
    listKbVideos: build.query<KbVideoListResponse, KbVideoListQuery>({
      query: (args) => ({
        method: 'GET',
        url: '/support/videos',
        params: {
          ...(args.q ? { q: args.q } : {}),
          ...(args.category ? { category: args.category } : {}),
          // '' is meaningful: it selects the ungrouped films.
          ...(args.group !== undefined ? { group: args.group } : {}),
          ...(args.theme ? { theme: args.theme } : {}),
          ...(args.lang ? { lang: args.lang } : {}),
          ...(args.strict ? { strict: 'true' } : {}),
          ...(args.limit !== undefined ? { limit: args.limit } : {}),
          ...(args.offset !== undefined ? { offset: args.offset } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.videos.map(({ id }) => ({
                type: 'KbVideo' as const,
                id,
              })),
              { type: 'KbVideo', id: 'LIST' },
            ]
          : [{ type: 'KbVideo', id: 'LIST' }],
    }),

    /**
     * Every film in any state, for the staff catalogue. The list above is the
     * library, which is published films only, so a draft made an hour ago is
     * invisible to it. Never cached across a mutation: this is the page people
     * publish and delete from.
     */
    listAllKbVideos: build.query<KbVideoCatalogueResponse, void>({
      query: () => ({ method: 'GET', url: '/support/videos/all' }),
      providesTags: [
        { type: 'KbVideo', id: 'CATALOGUE' },
        { type: 'KbVideo', id: 'LIST' },
      ],
    }),

    // Facet counts and the section layout. Separate from the list so filtering
    // never changes which facets are on offer.
    getKbVideoFacets: build.query<
      KbVideoFacetsResponse,
      { arrange?: 'group' | 'category' }
    >({
      query: (args) => ({
        method: 'GET',
        url: '/support/videos/facets',
        params: { ...(args.arrange ? { arrange: args.arrange } : {}) },
      }),
      providesTags: [{ type: 'KbVideo', id: 'FACETS' }],
    }),

    getKbVideo: build.query<
      KbVideoResponse,
      { id: string; theme?: 'dark' | 'light'; lang?: string }
    >({
      query: ({ id, theme, lang }) => ({
        method: 'GET',
        url: `/support/videos/${id}`,
        params: {
          ...(theme ? { theme } : {}),
          ...(lang ? { lang } : {}),
        },
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'KbVideo', id }],
    }),

    getKbVideoTranscript: build.query<
      KbVideoTranscriptResponse,
      { id: string; lang?: string }
    >({
      query: ({ id, lang }) => ({
        method: 'GET',
        url: `/support/videos/${id}/transcript`,
        params: { ...(lang ? { lang } : {}) },
      }),
    }),

    // Ranked matches with no filters, for "which film answers this question".
    // What the support agent surface asks when a customer describes a problem.
    searchKbVideos: build.query<
      KbVideoSearchResponse,
      { q: string; limit?: number; theme?: 'dark' | 'light'; lang?: string }
    >({
      query: ({ q, limit, theme, lang }) => ({
        method: 'GET',
        url: '/support/videos/search',
        params: {
          q,
          ...(limit !== undefined ? { limit } : {}),
          ...(theme ? { theme } : {}),
          ...(lang ? { lang } : {}),
        },
      }),
    }),

    // The S3 object tags actually on the files, for checking the bucket agrees
    // with the manifest. Staff only, and expected to report per-file errors
    // rather than fail: the bucket's cross-account policy does not grant
    // GetObjectTagging yet.
    getKbVideoTags: build.query<KbVideoTagsResponse, { id: string }>({
      query: ({ id }) => ({
        method: 'GET',
        url: `/support/videos/${id}/tags`,
      }),
    }),

    // Create or replace. The files must already be in the bucket; anything
    // absent comes back in `missing` as a warning rather than a rejection.
    upsertKbVideo: build.mutation<KbVideoUpsertResponse, KbVideoInput>({
      query: (body) => ({ method: 'POST', url: '/support/videos', data: body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'KbVideo', id },
        { type: 'KbVideo', id: 'LIST' },
        { type: 'KbVideo', id: 'FACETS' },
      ],
    }),

    patchKbVideo: build.mutation<
      KbVideoUpsertResponse,
      { id: string; patch: Partial<Omit<KbVideoInput, 'id'>> }
    >({
      query: ({ id, patch }) => ({
        method: 'PATCH',
        url: `/support/videos/${id}`,
        data: patch,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'KbVideo', id },
        { type: 'KbVideo', id: 'LIST' },
        { type: 'KbVideo', id: 'FACETS' },
      ],
    }),

    // Removes the manifest entry, which takes the film off every surface and
    // leaves the bytes in place. `purge` deletes the files too and is not
    // reversible.
    deleteKbVideo: build.mutation<
      KbVideoDeleteResponse,
      { id: string; purge?: boolean }
    >({
      query: ({ id, purge }) => ({
        method: 'DELETE',
        url: `/support/videos/${id}`,
        params: { ...(purge ? { purge: 'true' } : {}) },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'KbVideo', id },
        { type: 'KbVideo', id: 'LIST' },
        { type: 'KbVideo', id: 'CATALOGUE' },
        { type: 'KbVideo', id: 'FACETS' },
      ],
    }),

    // Ranked neighbours for the watch page. `upNext` is its own field rather
    // than a flagged row, because the page renders it as its own block, and it
    // is never repeated inside `related`, so the two need no dedupe. A null
    // `upNext` means the film has no successor, which is also what turns
    // autoplay off for it.
    //
    // A 404 is expected while the endpoint is being built: the query bails out
    // of retries on any 4xx (`shouldBailout`), and the watch page renders
    // without the column rather than waiting on it.
    getKbVideoRelated: build.query<
      KbVideoRelatedResponse,
      { id: string; theme?: KbVideoTheme; lang?: string; limit?: number }
    >({
      query: ({ id, theme, lang, limit }) => ({
        method: 'GET',
        url: `/support/videos/${id}/related`,
        params: {
          ...(theme ? { theme } : {}),
          ...(lang ? { lang } : {}),
          ...(limit !== undefined ? { limit } : {}),
        },
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'KbVideo', id }],
    }),

    // Watch tracking. Fire-and-forget from the player: nothing on screen
    // depends on the reply, so no tags are invalidated and no refetch follows.
    // The unload flush does NOT go through here: a request started during
    // pagehide is not guaranteed to complete unless it is a keepalive fetch, and
    // sendBeacon cannot carry the Authorization header this endpoint requires.
    // See use-watch-tracking.ts.
    postKbVideoEvents: build.mutation<
      KbWatchEventsResponse,
      KbWatchEventsRequest
    >({
      query: (body) => ({
        method: 'POST',
        url: '/support/videos/events',
        data: body,
      }),
    }),

    // Staff analytics for the admin panel. Both share the KbVideo tag so a
    // publish or delete refreshes them, but no mutation here ever invalidates
    // them: a viewer's own posts must not refetch a staff table.
    getKbVideoStats: build.query<
      KbVideoStatsResponse,
      { orgId?: string; recent?: number }
    >({
      query: ({ orgId, recent }) => ({
        method: 'GET',
        url: '/support/videos/stats',
        params: {
          ...(orgId ? { orgId } : {}),
          ...(recent !== undefined ? { recent } : {}),
        },
      }),
      providesTags: [{ type: 'KbVideo', id: 'STATS' }],
    }),

    getKbVideoWatchers: build.query<
      KbVideoWatchersResponse,
      { id: string; orgId?: string }
    >({
      query: ({ id, orgId }) => ({
        method: 'GET',
        url: `/support/videos/${id}/watchers`,
        params: { ...(orgId ? { orgId } : {}) },
      }),
      providesTags: (_r, _e, { id }) => [
        { type: 'KbVideo', id: `WATCHERS:${id}` },
      ],
    }),

    // ── Uploading a film somebody recorded ──────────────────────────
    // Two calls, because the file goes straight from the browser to S3
    // and never through trunk. This one hands back a presigned PUT; the
    // browser must send the returned headers verbatim, because the ACL
    // is part of the signature and S3 rejects a PUT without it.
    createKbVideoUploadUrl: build.mutation<
      {
        key: string
        uploadUrl: string
        headers: Record<string, string>
        url: string
      },
      { contentType: string }
    >({
      query: (body) => ({
        method: 'POST',
        url: '/support/videos/upload-url',
        data: body,
      }),
    }),

    // The slow one: trunk fetches the upload back, converts a mov,
    // transcribes the narration and publishes. Seconds, not
    // milliseconds, so the form says what it is doing.
    ingestKbVideo: build.mutation<KbVideoIngestResponse, KbVideoIngestRequest>({
      query: (body) => ({
        method: 'POST',
        url: '/support/videos/ingest',
        data: body,
      }),
      invalidatesTags: [
        { type: 'KbVideo', id: 'LIST' },
        { type: 'KbVideo', id: 'CATALOGUE' },
        { type: 'KbVideo', id: 'FACETS' },
      ],
    }),

    // ── Making films ────────────────────────────────────────────────
    // A production is the request plus everything that happened making it.
    // All staff only on the trunk side. Tags: one for the list, one per
    // production; a production that finishes adds a film, so approve and
    // retry also invalidate the library.

    createKbVideoProduction: build.mutation<
      KbVideoProductionCreateResponse,
      KbProductionSpecInput
    >({
      query: (spec) => ({
        method: 'POST',
        url: '/support/videos/productions',
        data: spec,
      }),
      invalidatesTags: [
        { type: 'KbVideo', id: 'PRODUCTIONS' },
        { type: 'KbVideo', id: 'LIST' },
      ],
    }),

    listKbVideoProductions: build.query<
      KbVideoProductionListResponse,
      { status?: string; limit?: number; videoId?: string }
    >({
      query: ({ status, limit, videoId }) => ({
        method: 'GET',
        url: '/support/videos/productions',
        params: {
          ...(status ? { status } : {}),
          ...(limit !== undefined ? { limit } : {}),
          // One film's runs, newest first: how the catalogue finds the request
          // to make that film again from.
          ...(videoId ? { videoId } : {}),
        },
      }),
      providesTags: [{ type: 'KbVideo', id: 'PRODUCTIONS' }],
    }),

    getKbVideoProduction: build.query<
      KbVideoProductionResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        method: 'GET',
        url: `/support/videos/productions/${id}`,
      }),
      providesTags: (_r, _e, { id }) => [
        { type: 'KbVideo', id: `PRODUCTION:${id}` },
      ],
    }),

    getKbVideoVoices: build.query<KbVideoVoicesResponse, void>({
      query: () => ({
        method: 'GET',
        url: '/support/videos/productions/voices',
      }),
    }),

    /** Release a manual-approval production, optionally with the edited script. */
    approveKbVideoProduction: build.mutation<
      { production: KbVideoProduction; mode: 'queue' | 'inline' },
      { id: string; script?: KbProductionScript }
    >({
      query: ({ id, script }) => ({
        method: 'POST',
        url: `/support/videos/productions/${id}/approve`,
        data: script ? { script } : {},
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'KbVideo', id: `PRODUCTION:${id}` },
        { type: 'KbVideo', id: 'PRODUCTIONS' },
      ],
    }),

    /** Run a failed production again from the first stage without output. */
    retryKbVideoProduction: build.mutation<
      { production: KbVideoProduction; mode: 'queue' | 'inline' },
      { id: string }
    >({
      query: ({ id }) => ({
        method: 'POST',
        url: `/support/videos/productions/${id}/retry`,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'KbVideo', id: `PRODUCTION:${id}` },
        { type: 'KbVideo', id: 'PRODUCTIONS' },
      ],
    }),

    /**
     * The same film again: a new production for the same record, so the new
     * cuts replace the old under the same id, URL and watch figures.
     *
     * `spec` is the edited request. Anything in it replaces what was asked for
     * last time (a new brief, the dark screens that were missed, a different
     * voice); anything left out is kept; an optional field sent as null is
     * cleared for the writer to fill in again. Sent empty, it is a plain remake.
     */
    reproduceKbVideoProduction: build.mutation<
      KbVideoProductionCreateResponse,
      { id: string; spec?: KbProductionRemakeInput }
    >({
      query: ({ id, spec }) => ({
        method: 'POST',
        url: `/support/videos/productions/${id}/again`,
        data: spec ?? {},
      }),
      invalidatesTags: [
        { type: 'KbVideo', id: 'PRODUCTIONS' },
        { type: 'KbVideo', id: 'CATALOGUE' },
      ],
    }),

    cancelKbVideoProduction: build.mutation<
      { production: KbVideoProduction },
      { id: string }
    >({
      query: ({ id }) => ({
        method: 'POST',
        url: `/support/videos/productions/${id}/cancel`,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'KbVideo', id: `PRODUCTION:${id}` },
        { type: 'KbVideo', id: 'PRODUCTIONS' },
      ],
    }),

    // ── Scheduled tasks ──────────────────────────────────────────────────────
    listScheduledTasks: build.query<
      { tasks: ScheduledTask[]; presets: ScheduledTaskPreset[] },
      { orgId: string; serial?: string | null; owner?: 'me' | string | null }
    >({
      query: ({ orgId, serial, owner }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/scheduled-tasks`,
        params: { ...(serial ? { serial } : {}), ...(owner ? { owner } : {}) },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.tasks.map(({ id }) => ({
                type: 'ScheduledTask' as const,
                id,
              })),
              { type: 'ScheduledTask', id: 'LIST' },
            ]
          : [{ type: 'ScheduledTask', id: 'LIST' }],
    }),
    proposeScheduledTask: build.mutation<
      | { ok: true; setup: ScheduledTaskSetup; interpreted?: boolean }
      | { ok: false; error: string },
      { orgId: string } & ProposeScheduledTaskBody
    >({
      query: ({ orgId, ...body }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/scheduled-tasks/propose`,
        data: body,
      }),
    }),
    createScheduledTask: build.mutation<
      { task: ScheduledTask },
      {
        orgId: string
        setup: ScheduledTaskSetup
        owner?: { oauth_id: string; name: string | null; email?: string | null }
      }
    >({
      query: ({ orgId, ...body }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/scheduled-tasks`,
        data: body,
      }),
      invalidatesTags: [{ type: 'ScheduledTask', id: 'LIST' }],
    }),
    getScheduledTask: build.query<
      { task: ScheduledTask; runs: ScheduledTaskRun[] },
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/scheduled-tasks/${id}`,
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'ScheduledTask', id }],
    }),
    updateScheduledTask: build.mutation<
      { task: ScheduledTask },
      {
        orgId: string
        id: string
        name?: string
        instructions?: string | null
        cron?: string
        timezone?: string
        deliver_to?: ScheduledDeliverTo[]
        may_act?: boolean
        owner?: { oauth_id: string; name: string | null; email?: string | null }
      }
    >({
      query: ({ orgId, id, ...body }) => ({
        method: 'PATCH',
        url: `/organisations/${orgId}/scheduled-tasks/${id}`,
        data: body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'ScheduledTask', id },
        { type: 'ScheduledTask', id: 'LIST' },
      ],
    }),
    deleteScheduledTask: build.mutation<
      { ok: true },
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'DELETE',
        url: `/organisations/${orgId}/scheduled-tasks/${id}`,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'ScheduledTask', id },
        { type: 'ScheduledTask', id: 'LIST' },
      ],
    }),
    setScheduledTaskStatus: build.mutation<
      { task: ScheduledTask },
      { orgId: string; id: string; action: 'pause' | 'resume' }
    >({
      query: ({ orgId, id, action }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/scheduled-tasks/${id}/${action}`,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'ScheduledTask', id },
        { type: 'ScheduledTask', id: 'LIST' },
      ],
    }),
    runScheduledTaskNow: build.mutation<
      { scheduled_fire_at: string },
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/scheduled-tasks/${id}/run`,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'ScheduledTask', id }],
    }),

    // ── Workflows ─────────────────────────────────────────────────────────────
    listWorkflows: build.query<
      { workflows: WorkflowRef[] },
      { orgId: string; serial?: string | null; status?: string | null }
    >({
      query: ({ orgId, serial, status }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/workflows`,
        params: {
          ...(serial ? { serial } : {}),
          ...(status ? { status } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.workflows.map(({ id }) => ({
                type: 'Workflow' as const,
                id,
              })),
              { type: 'Workflow', id: 'LIST' },
            ]
          : [{ type: 'Workflow', id: 'LIST' }],
    }),
    getWorkflowDefinition: build.query<
      { definition: WorkflowDefinition },
      { orgId: string; definitionId: string }
    >({
      query: ({ orgId, definitionId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/workflows/definitions/${encodeURIComponent(definitionId)}`,
      }),
      providesTags: (_r, _e, { definitionId }) => [
        { type: 'Workflow', id: `DEF:${definitionId}` },
      ],
    }),
    compileWorkflow: build.mutation<
      { workflow: WorkflowRef; job: CompileJob },
      {
        orgId: string
        text: string
        name?: string | null
        serial?: string | null
        lineage_id?: string | null
      }
    >({
      query: ({ orgId, ...body }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workflows/compile`,
        data: body,
      }),
      invalidatesTags: [{ type: 'Workflow', id: 'LIST' }],
    }),
    activateWorkflow: build.mutation<
      { workflow: WorkflowRef },
      {
        orgId: string
        lineageId: string
        trigger?: WorkflowTrigger
        may_act?: boolean
        status?: 'active' | 'archived' | 'draft'
      }
    >({
      query: ({ orgId, lineageId, ...body }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workflows/${encodeURIComponent(lineageId)}/activate`,
        data: body,
      }),
      invalidatesTags: (_r, _e, { lineageId }) => [
        { type: 'Workflow', id: lineageId },
        { type: 'Workflow', id: 'LIST' },
      ],
    }),
    runWorkflow: build.mutation<
      { run: WorkflowRunRef },
      { orgId: string; lineageId: string; enquiry_id: string }
    >({
      query: ({ orgId, lineageId, enquiry_id }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workflows/${encodeURIComponent(lineageId)}/run`,
        data: { enquiry_id },
      }),
    }),
    updateWorkflowSettings: build.mutation<
      { workflow: WorkflowRef },
      {
        orgId: string
        lineageId: string
        name?: string
        description?: string | null
        rules?: WorkflowRules | null
      }
    >({
      query: ({ orgId, lineageId, ...body }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workflows/${encodeURIComponent(lineageId)}/settings`,
        data: body,
      }),
      invalidatesTags: (_r, _e, { lineageId }) => [
        { type: 'Workflow', id: lineageId },
        { type: 'Workflow', id: 'LIST' },
      ],
    }),
    skipWorkflowWait: build.mutation<
      { run: WorkflowRunRef },
      { orgId: string; runId: string }
    >({
      query: ({ orgId, runId }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workflows/runs/${encodeURIComponent(runId)}/skip-wait`,
      }),
    }),
    stopWorkflowRun: build.mutation<
      { run: WorkflowRunRef },
      { orgId: string; runId: string }
    >({
      query: ({ orgId, runId }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/workflows/runs/${encodeURIComponent(runId)}/stop`,
      }),
    }),
    getAssetViewUrl: build.query<
      AssetViewUrlResponse,
      { orgId: string; assetId: string }
    >({
      query: ({ orgId, assetId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/assets/${assetId}/view-url`,
      }),
    }),
  }),
})

export default trunkApi
